import axios from 'axios';
import { getUrl } from '../service-utils';
import authHeader from '../auth-header';
import {
  ITripComment,
  IAddCommentRequest,
  IUpdateCommentRequest,
  IAddCommentResponse,
  IGetCommentsResponse,
  IUpdateCommentResponse,
  ICommentFilters
} from '../../Models/trips/trip-comment.model';

/**
 * Trip Comment Service
 *
 * Handles all comment operations for trips and their entities.
 * This service provides a unified comment system replacing the old
 * embedded photo comments.
 *
 * API Endpoints:
 * - GET    /api/v1/trips/:tripId/comments - Get comments with optional filters
 * - POST   /api/v1/trips/:tripId/comments - Add a new comment
 * - PATCH  /api/v1/trips/:tripId/comments/:commentId - Update a comment
 * - DELETE /api/v1/trips/:tripId/comments/:commentId - Delete a comment
 */
class TripCommentService {
  private API_URL: string;

  constructor() {
    this.API_URL = getUrl();
  }

  /**
   * Get comments for a trip entity
   * @param tripId - The trip ID
   * @param filters - Optional filters for entityType and entityId
   * @returns Promise<ITripComment[]>
   */
  public async getComments(
    tripId: string,
    filters?: ICommentFilters
  ): Promise<ITripComment[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.entityType) {
        params.append('entityType', filters.entityType);
      }
      if (filters?.entityId) {
        params.append('entityId', filters.entityId);
      }

      const queryString = params.toString();
      const url = queryString
        ? `${this.API_URL}trips/${tripId}/comments?${queryString}`
        : `${this.API_URL}trips/${tripId}/comments`;

      const response = await axios.get<IGetCommentsResponse>(url, {
        headers: authHeader()
      });

      return response.data.data.comments;
    } catch (error: any) {
      console.error(
        'Error fetching comments:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Add a new comment to a trip entity
   * @param tripId - The trip ID
   * @param data - Comment data (entityType, entityId, comment text)
   * @returns Promise<ITripComment>
   */
  public async addComment(
    tripId: string,
    data: IAddCommentRequest
  ): Promise<ITripComment> {
    try {
      // Validate comment text
      const trimmedComment = data.comment.trim();
      if (!trimmedComment) {
        throw new Error('Comment cannot be empty');
      }
      if (trimmedComment.length > 1000) {
        throw new Error('Comment exceeds maximum length of 1000 characters');
      }

      const response = await axios.post<IAddCommentResponse>(
        `${this.API_URL}trips/${tripId}/comments`,
        {
          ...data,
          comment: trimmedComment
        },
        { headers: authHeader() }
      );

      return response.data.data.comment;
    } catch (error: any) {
      console.error(
        'Error adding comment:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Update an existing comment
   * @param tripId - The trip ID
   * @param commentId - The comment ID
   * @param data - Updated comment data
   * @returns Promise<ITripComment>
   */
  public async updateComment(
    tripId: string,
    commentId: string,
    data: IUpdateCommentRequest
  ): Promise<ITripComment> {
    try {
      // Validate comment text
      const trimmedComment = data.comment.trim();
      if (!trimmedComment) {
        throw new Error('Comment cannot be empty');
      }
      if (trimmedComment.length > 1000) {
        throw new Error('Comment exceeds maximum length of 1000 characters');
      }

      const response = await axios.patch<IUpdateCommentResponse>(
        `${this.API_URL}trips/${tripId}/comments/${commentId}`,
        {
          comment: trimmedComment
        },
        { headers: authHeader() }
      );

      return response.data.data.comment;
    } catch (error: any) {
      console.error(
        'Error updating comment:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Delete a comment (soft delete)
   * @param tripId - The trip ID
   * @param commentId - The comment ID
   * @returns Promise<void>
   */
  public async deleteComment(tripId: string, commentId: string): Promise<void> {
    try {
      await axios.delete(
        `${this.API_URL}trips/${tripId}/comments/${commentId}`,
        { headers: authHeader() }
      );
    } catch (error: any) {
      console.error(
        'Error deleting comment:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }
}

const tripCommentServiceInstance = new TripCommentService();
export default tripCommentServiceInstance;
